import { useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { TooltipCard, TooltipLine, TOOLTIP_COLORS } from './ui/TooltipCard';

const COLORS = {
  slate100: '#f1f5f9',
  slate400: TOOLTIP_COLORS.slate400,
  slate500: TOOLTIP_COLORS.slate500,
  slate700: TOOLTIP_COLORS.slate700,
  secondaryRed: '#c43838',
};

const TICK_FONT_SIZE = 12;

const SERIES = [
  { key: 'impressions', label: 'Views', color: COLORS.slate700, legendTour: 'legend-toggle-impressions' },
  { key: 'views', label: 'Clicks', color: COLORS.slate500 },
  { key: 'seen', label: 'Seen', color: COLORS.slate400 },
];

const SERIES_ORDER = Object.fromEntries(SERIES.map((s, i) => [s.key, i]));
const SERIES_LABELS = Object.fromEntries(SERIES.map(s => [s.key, s.label]));

function bucketKeyToDate(key, granularity) {
  return granularity === 'month' ? new Date(`${key}-02T12:00:00Z`) : new Date(`${key}T12:00:00Z`);
}

function formatXTick(key, granularity) {
  try {
    const d = bucketKeyToDate(key, granularity);
    if (granularity === 'month') {
      return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });
    }
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  } catch {
    return key;
  }
}

function formatTooltipLabel(key, granularity) {
  try {
    const d = bucketKeyToDate(key, granularity);
    if (granularity === 'month') {
      return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
    }
    if (granularity === 'week') {
      return `Week of ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}`;
    }
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  } catch {
    return key;
  }
}

function ChartTooltip({ active, payload, label, granularity, showOpenings, showImpressionsStart, firstImpressionBucket }) {
  if (!active || !payload?.length) return null;

  const sorted = payload
    .filter(entry => entry.value != null)
    .sort((a, b) => SERIES_ORDER[a.dataKey] - SERIES_ORDER[b.dataKey]);
  const openings = showOpenings ? payload[0]?.payload?.openings ?? [] : [];
  const isImpressionsStart = showImpressionsStart && firstImpressionBucket && label === firstImpressionBucket;

  if (!sorted.length && !openings.length && !isImpressionsStart) return null;

  return (
    <TooltipCard minWidth={160}>
      <TooltipLine weight={600}>
        {formatTooltipLabel(label, granularity)}
      </TooltipLine>

      {sorted.map(entry => (
        <TooltipLine key={entry.dataKey} color={entry.stroke}>
          {SERIES_LABELS[entry.dataKey]}: {entry.value.toLocaleString()}
        </TooltipLine>
      ))}

      {openings.length > 0 && (
        <TooltipLine color={COLORS.secondaryRed} weight={600}>
          Opening: {openings.join(', ')}
        </TooltipLine>
      )}

      {isImpressionsStart && (
        <TooltipLine color={COLORS.slate700} weight={600}>
          Views tracking began
        </TooltipLine>
      )}
    </TooltipCard>
  );
}

function LegendToggle({ active, onClick, marker, label, color, ...rest }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 cursor-pointer select-none transition-opacity focus:outline-none ${active ? 'opacity-100' : 'opacity-30'}`}
      {...rest}
    >
      {marker}
      <span className="text-sm font-medium" style={{ color }}>{label}</span>
    </button>
  );
}

function AnalyticsChart({ chartData, granularity, loading, visibleLines, onToggleLine, impressionsLaunchBucket }) {
  const openingMarkers = chartData.filter(d => d.openings.length > 0);

  const tooltipContent = useCallback(
    props => (
      <ChartTooltip
        {...props}
        granularity={granularity}
        showOpenings={visibleLines.openings}
        showImpressionsStart={visibleLines.impressions}
        firstImpressionBucket={impressionsLaunchBucket}
      />
    ),
    [granularity, visibleLines.openings, visibleLines.impressions, impressionsLaunchBucket]
  );

  return (
    <div className="bg-white rounded-2xl p-6 border border-secondary-red/30 hover:border-secondary-red transition-colors">
      <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-2 mb-2">
        {SERIES.map(({ key, label, color, legendTour }) => (
          <LegendToggle
            key={key}
            data-tour={legendTour}
            active={visibleLines[key]}
            onClick={() => onToggleLine(key)}
            marker={<span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: color }} />}
            label={`${label} Over Time`}
            color={color}
          />
        ))}
        {openingMarkers.length > 0 && (
          <LegendToggle
            active={visibleLines.openings}
            onClick={() => onToggleLine('openings')}
            marker={<span className="w-3 shrink-0 flex justify-center text-secondary-red text-lg leading-none">▲</span>}
            label="Show Opening"
            color={COLORS.secondaryRed}
          />
        )}
      </div>

      <div>
        {loading ? (
          <div className="flex justify-center items-center h-[360px]">
            <span className="loading loading-spinner loading-lg text-secondary-red" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={chartData} margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate100} />
              <XAxis
                dataKey="date"
                tick={({ x, y, payload }) => (
                  <text
                    x={x}
                    y={y}
                    dy={10}
                    textAnchor="end"
                    transform={`rotate(-35, ${x}, ${y})`}
                    fontSize={TICK_FONT_SIZE}
                    fill={COLORS.slate400}
                  >
                    {formatXTick(payload.value, granularity)}
                  </text>
                )}
                height={30}
                axisLine={{ stroke: COLORS.slate400 }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: TICK_FONT_SIZE, fill: COLORS.slate400 }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
                domain={[0, dataMax => Math.ceil(dataMax * 1.15) || 1]}
                width={32}
              />
              <Tooltip content={tooltipContent} />
              {visibleLines.impressions && (
                <ReferenceLine
                  x={impressionsLaunchBucket}
                  stroke={COLORS.slate700}
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                />
              )}
              {SERIES.filter(({ key }) => visibleLines[key]).map(({ key, color }) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, fill: color }}
                  isAnimationActive={false}
                />
              ))}
              {visibleLines.openings && openingMarkers.map(({ date }) => (
                <ReferenceLine
                  key={date}
                  x={date}
                  stroke={COLORS.secondaryRed}
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                  label={{ value: '▲', position: 'top', fill: COLORS.secondaryRed, fontSize: 10 }}
                  isFront
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

export default AnalyticsChart;